import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsLayout from './SettingsLayout';
import { API_BASE_URL } from '@/config/api';
import { cn } from '@/lib/utils';
import { Calculator, MessagesSquare, ListChecks, Code2, ArrowRight } from 'lucide-react';

/**
 * /hirewithus/settings/templates — the "Question templates" hub.
 *
 * A single settings tab that fans out to one builder per interview type. Each
 * type is a card; adding a new builder (Screening, Coding, …) is just adding a
 * card + flipping its status from 'soon' to 'active' — no layout/nav changes.
 * The individual builder pages (Aptitude, P&B) are reused as-is; cards link to
 * their existing routes.
 */
// Each builder gets its own colour so the hub reads as a set of distinct tools.
// Full class strings (not built dynamically) so Tailwind keeps them at build time.
const BUILDERS = [
  {
    key: 'aptitude',
    title: 'Aptitude',
    desc: 'MCQ and voice sections, built section by section.',
    Icon: Calculator,
    path: '/hirewithus/settings/aptitude-builder',
    countPath: '/api/aptitude/templates',
    status: 'active',
    tile: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  },
  {
    key: 'pnb',
    title: 'Personality & Behavioural',
    desc: 'Conversational question sets Sana asks live.',
    Icon: MessagesSquare,
    path: '/hirewithus/settings/pnb-builder',
    countPath: '/api/pnb/templates',
    status: 'active',
    tile: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  },
  {
    key: 'screening',
    title: 'Screening',
    desc: 'Role-specific screening questions.',
    Icon: ListChecks,
    status: 'soon',
    tile: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  },
  {
    key: 'coding',
    title: 'Coding',
    desc: 'Coding challenges and problems.',
    Icon: Code2,
    status: 'soon',
    tile: 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300',
  },
];

export default function TemplatesHubPage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({}); // { [key]: number }

  useEffect(() => {
    let cancelled = false;
    const load = async (key, path) => {
      try {
        const res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const n = Array.isArray(data.templates) ? data.templates.length : 0;
        if (!cancelled) setCounts((c) => ({ ...c, [key]: n }));
      } catch {
        /* count is a nice-to-have — never block the hub on it */
      }
    };
    BUILDERS.filter((b) => b.status === 'active' && b.countPath)
      .forEach((b) => load(b.key, b.countPath));
    return () => { cancelled = true; };
  }, []);

  return (
    <SettingsLayout
      activeKey="templates"
      title="Settings"
      description="Manage your company profile, interview panel, recruiter access, and interview question templates."
    >
      <div>
        <h2 className="text-lg font-semibold">Interview question builders</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-5">
          Build reusable question sets for every kind of interview. Pick one when scheduling.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          {BUILDERS.map((b) => {
            const Icon = b.Icon;
            const count = counts[b.key];

            if (b.status === 'active') {
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => navigate(b.path)}
                  className="group text-left rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-3">
                    <span className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', b.tile)}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="font-medium">{b.title}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{b.desc}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', b.badge)}>
                      {count === undefined
                        ? 'Templates'
                        : `${count} template${count === 1 ? '' : 's'}`}
                    </span>
                    <span className="text-sm text-primary font-medium inline-flex items-center gap-1">
                      Open <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </button>
              );
            }

            return (
              <div
                key={b.key}
                className="rounded-xl border border-dashed border-border bg-muted/20 p-4"
                aria-disabled="true"
              >
                <div className="flex items-center gap-3">
                  <span className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', b.tile)}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="font-medium text-muted-foreground">{b.title}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{b.desc}</p>
                <div className="mt-3">
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-medium">
                    Coming soon
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SettingsLayout>
  );
}
